import { apiFetch } from '@/lib/api'
import { ConnectivityService } from '@/lib/connectivity'
import {
  enqueueIntent,
  inFlightIntents,
  markDone,
  markFailed,
  markInFlight,
  pendingIntents,
  type IntentRecord,
  type IntentType,
} from '@/lib/intent-queue'
import {
  getIntent,
  getOfflineQueueItemByAnalysisId,
  listIntents,
  purgeDoneIntents,
  purgeFailedIntents,
  recoverStuckIntents,
  removeFromOfflineQueue,
  updateIntent,
  type IntentStatus,
} from '@/lib/db'
import { getInternalUserId } from '@/lib/create-analysis'
import { getPowerSync } from '@/lib/powersync/client'
import { logger } from '@/lib/monitoring'
import { trackIntent, trackTicket } from '@/lib/analytics-client'

const BACKOFF_SCHEDULE_MS = [30_000, 120_000, 600_000, 3_600_000] as const
const BACKOFF_CAP_MS = 6 * 60 * 60 * 1000
const MAX_ATTEMPTS = 20
const AUTH_401_THRESHOLD = 3
const AUTH_401_WINDOW_MS = 5 * 60 * 1000
const IDLE_TICK_MS = 30_000
// Progressive worker pause after network errors. iOS app fetches can wedge for
// structural reasons (memory, background) — a fixed 60s retry hammers the same
// failure. Scale by count of failures in the trailing window, reset on success.
const NETWORK_FAIL_WINDOW_MS = 5 * 60 * 1000
const NETWORK_PAUSE_SCHEDULE_MS = [30_000, 60_000, 120_000, 300_000, 600_000] as const
// iOS Safari (esp. in-app WKWebView) has no implicit fetch timeout — and WebKit
// has a documented history of fetch+FormData uploads hanging across
// backgrounding/cellular flips (bugs 211018, 5760; Workbox #2386/#2428).
// We bound every handler so a hung request can't wedge `tickLock` forever.
const HANDLER_FETCH_TIMEOUT_MS = 90_000          // per-fetch (uploads dominate)
const ANALYZE_FETCH_TIMEOUT_MS = 60_000          // run-analysis is JSON-only
const HANDLER_TICK_WATCHDOG_MS = 120_000         // hard wall-clock per handler
const STUCK_RECOVERY_INTERVAL_MS = 30_000        // independent of tickLock
const AUTH_CHANNEL = 'imki-auth'
const NEEDS_REAUTH_EVENT = 'needs-reauth'
const AUTH_RESUMED_EVENT = 'auth-resumed'

type HandlerResult =
  | { kind: 'done' }
  | { kind: 'retry'; error: string; retryAfterMs?: number }
  | { kind: 'terminal'; error: string }
  | { kind: 'network'; error: string }
  | { kind: 'auth' }

type Handler = (intent: IntentRecord) => Promise<HandlerResult>

interface JsonFetchOptions {
  tag: string
  terminalStatus?: Record<number, string>
  doneStatus?: number[]
  rateLimitedRetryMs?: number
  // Statuses that should retry quickly (not the standard 30s/2m/10m/1h schedule).
  // Useful for transient races (e.g. PowerSync row not yet uploaded → server 404).
  shortRetryStatus?: Record<number, string>
  // Override per-handler. Defaults to HANDLER_FETCH_TIMEOUT_MS (90s).
  timeoutMs?: number
}

async function jsonFetch(
  url: string,
  init: RequestInit,
  opts: JsonFetchOptions,
): Promise<HandlerResult> {
  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), opts.timeoutMs ?? HANDLER_FETCH_TIMEOUT_MS)
  try {
    // signal goes last so it always wins over any caller-provided signal in init.
    // apiFetch prefixes the backend origin + attaches the bearer token on native.
    const res = await apiFetch(url, { ...init, signal: ctrl.signal })
    if (res.status === 401) return { kind: 'auth' }
    if (opts.doneStatus?.includes(res.status)) return { kind: 'done' }
    const terminalError = opts.terminalStatus?.[res.status]
    if (terminalError) return { kind: 'terminal', error: terminalError }
    if (res.status === 429 && opts.rateLimitedRetryMs) {
      return { kind: 'retry', error: 'rate_limited', retryAfterMs: opts.rateLimitedRetryMs }
    }
    const shortError = opts.shortRetryStatus?.[res.status]
    if (shortError) return { kind: 'retry', error: shortError, retryAfterMs: 2_000 }
    if (!res.ok) return { kind: 'retry', error: `${opts.tag}:${res.status}` }
    return { kind: 'done' }
  } catch (err) {
    const name = (err as { name?: string } | null)?.name
    if (name === 'AbortError') return { kind: 'network', error: `${opts.tag}:timeout` }
    return { kind: 'network', error: String(err) }
  } finally {
    clearTimeout(timer)
  }
}

// Block until PowerSync has uploaded local writes, or timeoutMs elapses.
// Handlers that depend on a freshly-inserted row (analyses, tickets) call this
// first so the server doesn't 404 on a row that only exists client-side.
async function waitForPowerSyncUpload(timeoutMs = 4_000): Promise<void> {
  const ps = getPowerSync()
  if (!ps || !ps.currentStatus?.dataFlowStatus?.uploading) return
  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), timeoutMs)
  try {
    await ps.waitForStatus((s) => !s.dataFlowStatus?.uploading, ctrl.signal)
  } finally {
    clearTimeout(timer)
  }
}

// Stricter variant: block until the specific `analyses` row has no pending
// CRUD entry in the local queue. Watching the global `uploading` flag has a
// race — between `execute(INSERT)` returning and the CRUD batcher picking it
// up, `uploading` is still false, so waitForPowerSyncUpload returns
// immediately and the handler posts before PowerSync pushes the row. The last
// row in a burst hits this reliably: no queued work after it to keep
// `uploading` true. Here we poll ps_crud for entries matching this row id and
// trigger the uploader on each pass until it clears.
async function waitForAnalysisRowSync(analysisId: string, timeoutMs = 15_000): Promise<'synced' | 'timeout'> {
  const ps = getPowerSync()
  if (!ps) return 'synced'
  const deadline = Date.now() + timeoutMs
  while (Date.now() < deadline) {
    const pending = await ps.getAll<{ id: number }>(
      `SELECT id FROM ps_crud
        WHERE json_extract(data, '$.type') = 'analyses'
          AND json_extract(data, '$.id') = ?`,
      [analysisId],
    )
    if (pending.length === 0) return 'synced'
    try { (ps as unknown as { triggerCrudUpload?: () => void }).triggerCrudUpload?.() } catch { /* noop — just nudge the uploader */ }
    await new Promise((r) => setTimeout(r, 300))
  }
  return 'timeout'
}

const STUCK_INTENT_ALERT_AGE_MS = 24 * 60 * 60 * 1000

class IntentWorker {
  private started = false
  private tickLock: Promise<void> = Promise.resolve()
  private idleTimer: ReturnType<typeof setInterval> | null = null
  private recoveryTimer: ReturnType<typeof setInterval> | null = null
  private unsubscribeConnectivity: (() => void) | null = null
  private visibilityListener: (() => void) | null = null
  private broadcastChannel: BroadcastChannel | null = null
  private paused = false
  private recentAuthFailures: number[] = []
  private recentNetworkFailures: number[] = []
  private networkPausedUntil = 0
  private handlers: Partial<Record<IntentType, Handler>> = {}
  private lastMetrics: { depth: number; ageP95Ms: number } | null = null
  private lastLocalThumbPruneAt = 0
  private lastPurgeAt = 0
  private lastReconcileAt = 0

  constructor() {
    this.registerDefaultHandlers()
  }

  start() {
    if (this.started || typeof window === 'undefined') return
    this.started = true

    this.unsubscribeConnectivity = ConnectivityService.getInstance().subscribe((online) => {
      if (online) this.scheduleTick()
    })

    this.visibilityListener = () => {
      if (document.visibilityState === 'visible') {
        // iOS may have killed in-flight fetches while backgrounded — revive
        // stuck rows immediately, don't wait for the recovery timer.
        void recoverStuckIntents()
          .catch((err) => {
            logger.monitoring.warn('intent-worker: visibility-recover failed', { error: String(err) })
          })
          .finally(() => this.scheduleTick())
      }
    }
    document.addEventListener('visibilitychange', this.visibilityListener)

    try {
      this.broadcastChannel = new BroadcastChannel(AUTH_CHANNEL)
      this.broadcastChannel.onmessage = (event) => {
        if (event.data?.type === NEEDS_REAUTH_EVENT) this.paused = true
        if (event.data?.type === AUTH_RESUMED_EVENT) {
          this.paused = false
          this.recentAuthFailures = []
          this.scheduleTick()
        }
      }
    } catch { /* older browsers — no cross-tab coordination */ }

    this.idleTimer = setInterval(() => {
      if (ConnectivityService.getInstance().isOnline) this.scheduleTick()
    }, IDLE_TICK_MS)

    // Recovery runs OUTSIDE tickLock. If a handler wedges (iOS Safari fetch
    // hang) the tick chain is stuck, but this timer still flips in_flight rows
    // back to pending so the next non-wedged tick can pick them up.
    this.recoveryTimer = setInterval(() => {
      void recoverStuckIntents()
        .then((n) => { if (n > 0) this.scheduleTick() })
        .catch((err) => {
          logger.monitoring.warn('intent-worker: recovery failed', { error: String(err) })
        })
    }, STUCK_RECOVERY_INTERVAL_MS)

    // Force re-pend ALL in_flight intents at boot — they're crash survivors
    // by definition, no handler from this session owns them.
    void recoverStuckIntents(true).then(() => this.scheduleTick())

    const now = Date.now()

    // Auto-prune. Done intents age out fast (24h), terminally-failed stay a
    // bit longer (3d) for debugging, but neither accumulates forever. Gated
    // to hourly so HMR / tab-focus start() calls don't re-scan by-status twice.
    if (now - this.lastPurgeAt > 60 * 60 * 1000) {
      this.lastPurgeAt = now
      void Promise.all([
        purgeDoneIntents(24 * 60 * 60 * 1000),
        purgeFailedIntents(3 * 24 * 60 * 60 * 1000),
      ]).catch((err) => {
        // IDB purges can fail (db blocked / version mismatch). Without
        // visibility, dead intents accumulate forever and the queue grows
        // unbounded.
        logger.monitoring.warn('intent-worker: purge failed', { error: String(err) })
      })
    }

    // Re-enqueue orphaned analyses whose run-analysis intent never landed in
    // IDB (iOS tap-dropped mid-handler). Gated to 5 min so HMR/auth-flip
    // restart cycles don't re-scan SQLite + listIntents every time.
    if (now - this.lastReconcileAt > 5 * 60 * 1000) {
      this.lastReconcileAt = now
      void reconcilePendingAnalyses().catch((err) => {
        logger.monitoring.warn('intent-worker: reconcile failed', { error: String(err) })
      })
    }
  }

  stop() {
    if (!this.started) return
    this.started = false
    this.unsubscribeConnectivity?.()
    if (this.visibilityListener) {
      document.removeEventListener('visibilitychange', this.visibilityListener)
    }
    this.broadcastChannel?.close()
    this.broadcastChannel = null
    if (this.idleTimer) clearInterval(this.idleTimer)
    this.idleTimer = null
    if (this.recoveryTimer) clearInterval(this.recoveryTimer)
    this.recoveryTimer = null
  }

  registerHandler(type: IntentType, handler: Handler) {
    this.handlers[type] = handler
  }

  resumeAfterReauth() {
    this.paused = false
    this.recentAuthFailures = []
    try { this.broadcastChannel?.postMessage({ type: AUTH_RESUMED_EVENT }) } catch { /* noop */ }
    this.scheduleTick()
  }

  /** Called from enqueueIntent callers that want the fast online path. */
  scheduleTick() {
    if (!this.started || this.paused) return
    this.tickLock = this.tickLock.then(() => this.runTick()).catch((err) => {
      logger.monitoring.error('intent-worker: tick error', { error: String(err) })
    })
  }

  private async runTick(): Promise<void> {
    if (this.paused) return
    if (!ConnectivityService.getInstance().isOnline) return
    if (Date.now() < this.networkPausedUntil) return

    await recoverStuckIntents()

    const pending = await pendingIntents()
    if (pending.length === 0) return

    pending.sort((a, b) => a.createdAt - b.createdAt)

    for (const intent of pending) {
      if (this.paused) return
      if (Date.now() < this.networkPausedUntil) return

      if (intent.nextRunAt && intent.nextRunAt > Date.now()) continue

      if (intent.dependsOn) {
        const dep = await getIntent(intent.dependsOn)
        if (!dep) {
          await markFailed(intent.id, `dependency-missing:${intent.dependsOn}`, undefined, true)
          this.notifyHandlerFailed(intent, 'dependency-missing')
          continue
        }
        if (dep.status === 'failed') {
          await markFailed(intent.id, `dependency-failed:${intent.dependsOn}`, undefined, true)
          this.notifyHandlerFailed(intent, `dependency-failed:${intent.dependsOn}`)
          continue
        }
        if (dep.status !== 'done') continue
      }

      const handler = this.handlers[intent.type]
      if (!handler) {
        await markFailed(intent.id, `no_handler:${intent.type}`, undefined, true)
        continue
      }

      await markInFlight(intent.id)
      const handlerStart = Date.now()
      const result: HandlerResult = await this.runHandlerWithWatchdog(handler, intent)
      const handlerLatencyMs = Date.now() - handlerStart

      if (result.kind === 'auth') {
        this.recordAuthFailure()
        await markFailed(intent.id, 'auth_401', Date.now() + 30_000, false)
        if (this.paused) return
        continue
      }

      if (result.kind === 'network') {
        let pauseMs = this.recordNetworkFailure()
        // Fast-path: when connectivity is clearly up, a network failure is
        // almost always a transient (Chrome dropped the response, iOS flipped
        // mid-fetch, etc.). Retry in a few seconds instead of the 30s offline-
        // flap default. The server-side upload is idempotent (imageUrl check)
        // so a quick retry either short-circuits to done or re-confirms cheaply.
        // Only applies on the FIRST failure in the window — repeated failures
        // still escalate through the offline-flap schedule.
        if (
          this.recentNetworkFailures.length === 1 &&
          ConnectivityService.getInstance().isOnline &&
          typeof navigator !== 'undefined' && navigator.onLine
        ) {
          pauseMs = 3_000
        }
        this.networkPausedUntil = Date.now() + pauseMs
        await markFailed(intent.id, result.error, Date.now() + pauseMs, false)
        logger.intents.info('retry', {
          intentId: intent.id,
          type: intent.type,
          attempt: intent.attempts + 1,
          nextRunAt: Date.now() + pauseMs,
          lastError: 'network',
          pauseMs,
          recentFailures: this.recentNetworkFailures.length,
        })
        return
      }

      if (result.kind === 'done') {
        // Clear the network-failure streak — a successful tick proves the
        // wedge condition has lifted, so the next blip shouldn't inherit a
        // 10-minute pause.
        this.recentNetworkFailures = []
        await markDone(intent.id)
        trackIntent('intent_completed', {
          intentId: intent.id,
          type: intent.type,
          attempts: intent.attempts + 1,
          latencyMs: Date.now() - intent.createdAt,
          handlerLatencyMs,
        })
        this.notifyHandlerCompleted(intent)
        continue
      }

      if (result.kind === 'terminal' || intent.attempts + 1 >= MAX_ATTEMPTS) {
        const failError = result.kind === 'terminal' ? result.error : 'max_attempts'
        await markFailed(intent.id, failError, undefined, true)
        trackIntent('intent_failed', {
          intentId: intent.id,
          type: intent.type,
          attempts: intent.attempts + 1,
          error: failError,
        })
        this.notifyHandlerFailed(intent, failError)
        logger.intents.error('intent-stuck', {
          intentId: intent.id,
          type: intent.type,
          attempts: intent.attempts + 1,
          error: failError,
        })
        void this.maybeReportStuck(intent, failError).catch((err) => {
          logger.intents.warn('maybeReportStuck failed', { intentId: intent.id, error: String(err) })
        })
        continue
      }

      const backoff = result.retryAfterMs ?? this.backoffFor(intent.attempts)
      await markFailed(intent.id, result.error, Date.now() + backoff, false)
      logger.intents.info('retry', {
        intentId: intent.id,
        type: intent.type,
        attempt: intent.attempts + 1,
        nextRunAt: Date.now() + backoff,
        lastError: result.error,
      })
    }

    await this.emitMetrics()
    await this.pruneStaleLocalThumbs().catch(() => { /* non-fatal */ })
  }

  // Opportunistic cleanup of the fast-feedback data-URL previews. Kept for an
  // hour after the server-side thumbnail should exist (Sharp runs post-upload
  // and is fast but non-instant) so SignedImage's onError can still fall back
  // during the brief _thumb.webp 404 window.
  private async pruneStaleLocalThumbs(): Promise<void> {
    const now = Date.now()
    if (now - this.lastLocalThumbPruneAt < 60 * 60 * 1000) return
    this.lastLocalThumbPruneAt = now
    const ps = getPowerSync()
    if (!ps) return
    await ps.execute(
      `DELETE FROM analysis_local_thumbs
       WHERE analysis_id IN (
         SELECT id FROM analyses
          WHERE "imageUrl" IS NOT NULL
            AND datetime("updatedAt") < datetime('now', '-1 hour')
       )`,
    )
  }

  // Wraps handler execution so a hung promise (iOS fetch wedge, IDB stall) can
  // never wedge `tickLock`. The orphaned handler may eventually settle after
  // we've returned `network` here — server endpoints are idempotent (uploads
  // upsert + early-return on imageUrl already set; analyze has its own dedupe)
  // so a duplicate completion is harmless.
  private async runHandlerWithWatchdog(handler: Handler, intent: IntentRecord): Promise<HandlerResult> {
    let watchdogTimer: ReturnType<typeof setTimeout> | null = null
    const watchdog = new Promise<HandlerResult>((resolve) => {
      watchdogTimer = setTimeout(() => {
        resolve({ kind: 'network', error: 'handler_watchdog_timeout' })
      }, HANDLER_TICK_WATCHDOG_MS)
    })
    const guarded = (async (): Promise<HandlerResult> => {
      try {
        return await handler(intent)
      } catch (err) {
        return { kind: 'retry', error: String(err) }
      }
    })()
    try {
      return await Promise.race([guarded, watchdog])
    } finally {
      if (watchdogTimer) clearTimeout(watchdogTimer)
    }
  }

  private async emitMetrics() {
    try {
      const [pending, inFlight] = await Promise.all([pendingIntents(), inFlightIntents()])
      const active = [...pending, ...inFlight]
      const depth = active.length
      const now = Date.now()
      const ages = active.map((i) => now - i.createdAt).sort((a, b) => a - b)
      const ageP95Ms = ages.length > 0 ? ages[Math.max(0, Math.ceil(ages.length * 0.95) - 1)] : 0

      // Silent when metrics haven't changed since last emit. Prevents log flood
      // both at idle (depth=0 steady) and at steady-load (depth=N unchanged).
      const last = this.lastMetrics
      if (last && last.depth === depth && last.ageP95Ms === ageP95Ms) return
      this.lastMetrics = { depth, ageP95Ms }

      const byType: Record<string, number> = {}
      for (const i of active) byType[i.type] = (byType[i.type] || 0) + 1

      logger.intents.info('intent-metrics', { depth, ageP95Ms, byType })
    } catch (err) {
      logger.intents.warn('emitMetrics failed', { error: String(err) })
    }
  }

  private async maybeReportStuck(intent: IntentRecord, error: string) {
    const ageMs = Date.now() - intent.createdAt
    if (ageMs < STUCK_INTENT_ALERT_AGE_MS) return
    try {
      await apiFetch('/api/internal/intent-stuck-alert', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          intentId: intent.id,
          intentType: intent.type,
          ageMs,
          attempts: intent.attempts + 1,
          error,
        }),
      })
    } catch { /* best-effort; server-side dedupe means missed POSTs retry next alert */ }
  }

  private backoffFor(attempt: number): number {
    if (attempt < BACKOFF_SCHEDULE_MS.length) return BACKOFF_SCHEDULE_MS[attempt]
    return BACKOFF_CAP_MS
  }

  private recordNetworkFailure(): number {
    const now = Date.now()
    this.recentNetworkFailures = this.recentNetworkFailures.filter(
      (t) => now - t < NETWORK_FAIL_WINDOW_MS,
    )
    this.recentNetworkFailures.push(now)
    const idx = Math.min(
      this.recentNetworkFailures.length - 1,
      NETWORK_PAUSE_SCHEDULE_MS.length - 1,
    )
    return NETWORK_PAUSE_SCHEDULE_MS[idx]
  }

  private recordAuthFailure() {
    const now = Date.now()
    this.recentAuthFailures = this.recentAuthFailures.filter((t) => now - t < AUTH_401_WINDOW_MS)
    this.recentAuthFailures.push(now)
    if (this.recentAuthFailures.length > AUTH_401_THRESHOLD * 2) {
      this.recentAuthFailures = this.recentAuthFailures.slice(-AUTH_401_THRESHOLD)
    }
    if (this.recentAuthFailures.length >= AUTH_401_THRESHOLD) {
      this.paused = true
      try { this.broadcastChannel?.postMessage({ type: NEEDS_REAUTH_EVENT }) } catch { /* noop */ }
      if (typeof window !== 'undefined') {
        try { window.dispatchEvent(new CustomEvent(NEEDS_REAUTH_EVENT)) } catch { /* noop */ }
      }
    }
  }

  private async notifyHandlerCompleted(intent: IntentRecord) {
    if (intent.type === 'send-ticket-email') {
      trackTicket('sent_completed', {
        ticketId: String(intent.payload.ticketId || ''),
        intentId: intent.id,
        latencyMs: Date.now() - intent.createdAt,
      })
    }
    if (intent.type === 'upload-image') {
      const analysisId = String(intent.payload.analysisId || '')
      if (analysisId) {
        // History/detail pages render `analysis.imageUrl || <local blob>`.
        // The server has the bytes now, but imageUrl only reaches local
        // SQLite via PowerSync sync-back — purging the blob before that
        // leaves the page with NEITHER source (blank image) for the whole
        // sync-back window, which on a slow native sync is the user-visible
        // "photo is blank after analysis" bug. Hold the blob until imageUrl
        // is locally visible; if it never lands within the wait, keep it —
        // a retained capture beats a blank photo (cost: one orphaned blob).
        try {
          if (await this.waitForImageUrlSyncback(analysisId)) {
            const item = await getOfflineQueueItemByAnalysisId(analysisId)
            if (item?.id != null) await removeFromOfflineQueue(item.id)
          }
        } catch { /* purge is best-effort */ }
      }
    }
    if (intent.type === 'upload-floor-plan') {
      const floorPlanId = String(intent.payload.floorPlanId || '')
      if (floorPlanId) {
        try {
          const item = await getOfflineQueueItemByAnalysisId(floorPlanId)
          if (item?.id != null) await removeFromOfflineQueue(item.id)
        } catch { /* purge is best-effort */ }
      }
    }
  }

  // True once `analyses.imageUrl` is visible in local SQLite (or the row is
  // gone, in which case the blob has no consumer). False = caller keeps blob.
  private async waitForImageUrlSyncback(analysisId: string, timeoutMs = 60_000): Promise<boolean> {
    const deadline = Date.now() + timeoutMs
    while (Date.now() < deadline) {
      const ps = getPowerSync()
      if (!ps) return false
      try {
        const row = await ps.getOptional<{ imageUrl: string | null }>(
          `SELECT "imageUrl" FROM analyses WHERE id = ?`,
          [analysisId],
        )
        if (!row) return true
        if (row.imageUrl) return true
      } catch { /* transient read failure — keep polling */ }
      await new Promise((r) => setTimeout(r, 1_000))
    }
    return false
  }

  private async notifyHandlerFailed(intent: IntentRecord, error: string) {
    if (intent.type === 'send-ticket-email') {
      trackTicket('sent_failed', {
        ticketId: String(intent.payload.ticketId || ''),
        intentId: intent.id,
        error,
      })
    }
    if (intent.type === 'upload-image' || intent.type === 'run-analysis') {
      const analysisId = String(intent.payload.analysisId || '')
      if (!analysisId) return
      const powersync = getPowerSync()
      if (!powersync) return
      try {
        await powersync.execute(
          `UPDATE analyses SET status = ?, last_error = ? WHERE id = ?`,
          ['failed', intent.type === 'upload-image' ? 'upload_failed' : 'analyze_failed', analysisId],
        )
      } catch (err) {
        logger.monitoring.warn('intent-worker: failed to mark analysis row failed', {
          analysisId,
          error: String(err),
        })
      }
    }
  }

  private registerDefaultHandlers() {
    this.registerHandler('upload-image', async (intent) => {
      const analysisId = String(intent.payload.analysisId || '')
      if (!analysisId) return { kind: 'terminal', error: 'missing_analysisId' }

      // Short-circuit if PowerSync already synced imageUrl back — the upload
      // landed server-side on a prior attempt whose response WebKit wedged.
      try {
        const ps = getPowerSync()
        if (ps) {
          const row = await ps.getOptional<{ imageUrl: string | null }>(
            `SELECT "imageUrl" FROM analyses WHERE id = ?`,
            [analysisId],
          )
          if (row?.imageUrl) return { kind: 'done' }
        }
      } catch { /* fall through — server-side is idempotent */ }

      const item = await getOfflineQueueItemByAnalysisId(analysisId)
      if (!item) {
        // Blob is gone. Either a sibling upload-image intent for the same
        // analysisId already succeeded and ran removeFromOfflineQueue, or the
        // user wiped storage. Re-check imageUrl with a short retry — PowerSync
        // hasn't necessarily synced the field back yet at the moment the
        // sibling completed. If it lands within the window, treat as done so
        // we don't falsely mark the row failed.
        try {
          const ps = getPowerSync()
          if (ps) {
            for (let i = 0; i < 5; i++) {
              const row = await ps.getOptional<{ imageUrl: string | null }>(
                `SELECT "imageUrl" FROM analyses WHERE id = ?`,
                [analysisId],
              )
              if (row?.imageUrl) return { kind: 'done' }
              await new Promise((r) => setTimeout(r, 500))
            }
          }
        } catch { /* fall through to terminal */ }
        return { kind: 'terminal', error: 'blob_missing' }
      }

      // Refuse to send empty bodies. iOS app fetch+FormData wedge (and rare
      // capture-path bugs) can persist a zero-byte blob in IDB; sending it
      // gets a 400 "image_required" from the server, intent goes terminal,
      // analyze gets 409 "no image", re-enqueues a fresh upload-image →
      // same empty blob → 400 forever. Catching it here breaks the loop on
      // the client side and avoids the round-trip entirely.
      const byteLen = item.image instanceof Blob
        ? item.image.size
        : (item.image as ArrayBuffer | Uint8Array)?.byteLength ?? 0
      if (!byteLen) {
        return { kind: 'terminal', error: 'empty_blob' }
      }

      // Avoid racing PowerSync: if the analyses row hasn't pushed yet, the
      // server returns 404. The per-row check below is stricter than the
      // global `uploading` flag and closes the last-photo-stuck window.
      await waitForAnalysisRowSync(analysisId)

      // Raw ArrayBuffer body (not FormData): iOS WebKit wedges multipart app
      // uploads (bugs 211018, 5760, 187461; RN #7564 malforms boundary).
      return jsonFetch(
        `/api/uploads/${analysisId}`,
        {
          method: 'POST',
          body: item.image,
          headers: { 'content-type': item.imageType || 'image/jpeg' },
        },
        {
          tag: 'upload',
          shortRetryStatus: { 404: 'row_not_synced' },
          // 400 = server couldn't decode the bytes (truncated PNG from a
          // legacy capture path, etc). Retrying sends the exact same bad
          // blob — mark terminal so the queue stops spinning on it.
          //
          // 403 = the account ran out (trial expired / budget spent /
          // subscription ended) while these photos sat in the queue. That will
          // never resolve by retrying, and without this a tester who shot 30
          // photos offline before their trial ended would fire 30 × 20 doomed
          // requests and an equal pile of stuck-intent alerts. Same call the
          // analyze handler already makes.
          terminalStatus: { 400: 'invalid_image', 403: 'account_blocked' },
        },
      )
    })

    this.registerHandler('run-analysis', async (intent) => {
      const analysisId = String(intent.payload.analysisId || '')
      if (!analysisId) return { kind: 'terminal', error: 'missing_analysisId' }
      // 409 means the server has no image: re-enqueue upload before retrying analyze.
      const ctrl = new AbortController()
      const timer = setTimeout(() => ctrl.abort(), ANALYZE_FETCH_TIMEOUT_MS)
      try {
        const res = await apiFetch(`/api/analyses/${analysisId}/analyze`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ retry: intent.attempts > 0 }),
          signal: ctrl.signal,
        })
        if (res.status === 401) return { kind: 'auth' }
        if (res.status === 202) return { kind: 'retry', error: 'in_progress', retryAfterMs: 15_000 }
        // 403: demo budget/expiry reached (or project access lost). Terminal —
        // retrying can't succeed and would hammer the server every few seconds.
        if (res.status === 403) return { kind: 'terminal', error: 'forbidden' }
        if (res.status === 409) {
          // If a prior upload-image attempt already terminated and marked the
          // row 'failed' (empty blob, invalid image, etc), don't re-enqueue
          // a fresh upload — there's no good blob to send and we'd just loop
          // 409 → enqueue → 400 → terminal → 409 forever, hammering the
          // server every few seconds. Treat this analyze intent as terminal
          // too; the row stays 'failed' and the user sees the red retry
          // strip exactly once.
          try {
            const ps = getPowerSync()
            const row = await ps?.getOptional<{ status: string | null }>(
              `SELECT status FROM analyses WHERE id = ?`,
              [analysisId],
            )
            if (row?.status === 'failed') {
              return { kind: 'terminal', error: 'upload_terminated' }
            }
          } catch { /* fall through to re-enqueue path */ }

          // Reuse an existing pending/in-flight upload-image intent for this
          // analysisId if one exists. Without this, two upload-image intents
          // race: first succeeds and removes the blob; the duplicate then
          // returns terminal:blob_missing and notifyHandlerFailed marks the
          // analyses row failed even though the upload landed. Reconciler
          // doesn't recover failed rows, so the user sees a stuck-failed row.
          const [pendingUp, inFlightUp] = await Promise.all([
            listIntents({ type: 'upload-image', status: 'pending' }),
            listIntents({ type: 'upload-image', status: 'in_flight' }),
          ])
          const existing = [...pendingUp, ...inFlightUp].find(
            (i) => String((i.payload as { analysisId?: unknown }).analysisId || '') === analysisId,
          )
          const uploadIntent = existing ?? await enqueueIntent('upload-image', { analysisId })
          await updateIntent(intent.id, { dependsOn: uploadIntent.id })
          return { kind: 'retry', error: 'image_missing', retryAfterMs: 5_000 }
        }
        if (!res.ok) return { kind: 'retry', error: `analyze:${res.status}` }
        return { kind: 'done' }
      } catch (err) {
        const name = (err as { name?: string } | null)?.name
        if (name === 'AbortError') return { kind: 'network', error: 'analyze:timeout' }
        return { kind: 'network', error: String(err) }
      } finally {
        clearTimeout(timer)
      }
    })

    this.registerHandler('send-ticket-email', async (intent) => {
      const ticketId = String(intent.payload.ticketId || '')
      if (!ticketId) return { kind: 'terminal', error: 'missing_ticketId' }
      // Avoid racing PowerSync: if the ticket row (or its defects) hasn't been
      // replicated yet, the server returns 404. Wait briefly for upload to drain.
      try { await waitForPowerSyncUpload() } catch { /* fall through — shortRetryStatus handles 404 */ }
      return jsonFetch(
        `/api/tickets/${ticketId}/send`,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            templateId: intent.payload.templateId,
            resend: intent.payload.resend === true,
            idempotencyKey: intent.id,
          }),
        },
        {
          tag: 'send',
          rateLimitedRetryMs: 600_000,
          shortRetryStatus: { 404: 'row_not_synced' },
          // 403 = the account ran out and outbound mail is closed. Retrying
          // twenty times can't change that; stop and surface it once.
          terminalStatus: { 403: 'account_blocked' },
        },
      )
    })

    this.registerHandler('upload-floor-plan', async (intent) => {
      const floorPlanId = String(intent.payload.floorPlanId || '')
      const projectId = String(intent.payload.projectId || '')
      if (!floorPlanId || !projectId) return { kind: 'terminal', error: 'missing_ids' }
      const item = await getOfflineQueueItemByAnalysisId(floorPlanId)
      if (!item) return { kind: 'terminal', error: 'blob_missing' }
      // Raw bytes (not FormData): iOS WebKit wedges multipart app uploads
      // during backgrounding (WebKit bugs 211018, 5760, 187461). Same
      // workaround as upload-image. Metadata moves to query params.
      const query = new URLSearchParams({
        id: floorPlanId,
        building: String(intent.payload.building || ''),
        floor: String(intent.payload.floor || ''),
        label: String(intent.payload.label || ''),
      }).toString()
      return jsonFetch(
        `/api/projects/${projectId}/floor-plans?${query}`,
        {
          method: 'POST',
          body: item.image,
          headers: { 'content-type': item.imageType || 'image/png' },
        },
        { tag: 'floor-plan-upload' },
      )
    })

    this.registerHandler('invite-user', async (intent) => {
      const email = String(intent.payload.email || '')
      const role = String(intent.payload.role || '')
      const name = intent.payload.name ? String(intent.payload.name) : undefined
      if (!email || !role) return { kind: 'terminal', error: 'missing_fields' }
      return jsonFetch(
        '/api/admin/users/invite',
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ email, role, name, idempotencyKey: intent.id }),
        },
        { tag: 'invite', terminalStatus: { 409: 'conflict_soft_deleted' } },
      )
    })

    this.registerHandler('delete-user', async (intent) => {
      const userId = String(intent.payload.userId || '')
      if (!userId) return { kind: 'terminal', error: 'missing_userId' }
      const purge = intent.payload.purge === true
      return jsonFetch(
        `/api/admin/users/${userId}${purge ? '?purge=true' : ''}`,
        {
          method: 'DELETE',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ idempotencyKey: intent.id }),
        },
        { tag: 'delete', doneStatus: [404] },
      )
    })

    this.registerHandler('delete-defect', async (intent) => {
      const defectId = String(intent.payload.defectId || '')
      if (!defectId) return { kind: 'terminal', error: 'missing_defectId' }
      return jsonFetch(
        `/api/defects/${defectId}`,
        {
          method: 'DELETE',
          headers: { 'content-type': 'application/json' },
        },
        { tag: 'delete-defect', doneStatus: [200, 204, 404], terminalStatus: { 403: 'unauthorized' } },
      )
    })

    this.registerHandler('upload-training-image', async (intent) => {
      const defectTypeId = String(intent.payload.defectTypeId || '')
      if (!defectTypeId) return { kind: 'terminal', error: 'missing_defectTypeId' }
      const filename = String(intent.payload.filename || `${intent.id}.jpg`)
      const blobKey = String(intent.payload.blobKey || intent.id)
      const item = await getOfflineQueueItemByAnalysisId(blobKey)
      if (!item) return { kind: 'terminal', error: 'blob_missing' }
      // Raw bytes (not FormData) — same iOS-WebKit reasoning as upload-image.
      const query = new URLSearchParams({
        defectTypeId,
        idempotencyKey: intent.id,
        filename,
      }).toString()
      return jsonFetch(
        `/api/defect-types/upload-training-image?${query}`,
        {
          method: 'POST',
          body: item.image,
          headers: { 'content-type': item.imageType || 'image/jpeg' },
        },
        { tag: 'training-image' },
      )
    })

    this.registerHandler('trigger-reminder-run', async (intent) => {
      return jsonFetch(
        '/api/cron/process-reminders',
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ idempotencyKey: intent.id }),
        },
        { tag: 'reminder-run', rateLimitedRetryMs: 600_000 },
      )
    })

    this.registerHandler('save-slides-settings', async (intent) => {
      const disabledSlides = intent.payload.disabledSlides
      const disabledLanguages = intent.payload.disabledLanguages
      if (!Array.isArray(disabledSlides) || !Array.isArray(disabledLanguages)) {
        return { kind: 'terminal', error: 'invalid_payload' }
      }
      return jsonFetch(
        '/api/slides-settings',
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ disabledSlides, disabledLanguages, idempotencyKey: intent.id }),
        },
        { tag: 'slides-settings' },
      )
    })

    this.registerHandler('report-dead-letter', async (intent) => {
      const deadLetterId = String(intent.payload.deadLetterId || '')
      if (!deadLetterId) return { kind: 'terminal', error: 'missing_deadLetterId' }
      return jsonFetch(
        '/api/dead-letters',
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(intent.payload),
        },
        { tag: 'dead-letter', terminalStatus: { 400: 'invalid_payload' } },
      )
    })

    this.registerHandler('generate-inspection-pdf', async (intent) => {
      const projectId = String(intent.payload.projectId || '')
      const inspectionId = String(intent.payload.inspectionId || '')
      if (!projectId || !inspectionId) return { kind: 'terminal', error: 'missing_ids' }
      return jsonFetch(
        `/api/projects/${projectId}/inspections/${inspectionId}/pdf`,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json', accept: 'application/json' },
          body: JSON.stringify({}),
        },
        { tag: 'pdf' },
      )
    })

  }
}

let worker: IntentWorker | null = null
export function getIntentWorker(): IntentWorker {
  if (!worker) worker = new IntentWorker()
  return worker
}

/**
 * Self-healing sweep: scans SQLite for analyses whose server-side lifecycle is
 * incomplete and has no active run-analysis intent, then enqueues one.
 *
 * Hard-bounded to keep the queue from flooding:
 *   - Only rows with `imageUrl` already set (server has the blob; otherwise
 *     run-analysis 409s and cascades into blob_missing failures).
 *   - Only rows created in the last 24h. Older orphans stay orphaned — user
 *     can re-trigger from history if they want the analysis back.
 *   - LIMIT 20 — a legit session rarely leaves >20 pending, and this caps the
 *     blast radius if something upstream goes wrong.
 *
 * Safe to run repeatedly — the listIntents check skips analyses that already
 * have a non-done run-analysis intent.
 */
const RECONCILE_LIMIT = 20
const RECONCILE_MAX_AGE_MS = 24 * 60 * 60 * 1000

export async function reconcilePendingAnalyses(): Promise<number> {
  const ps = getPowerSync()
  if (!ps) return 0
  // Scope to the signed-in user. Without this, every project member's device
  // volunteers to drive orphan rows it synced down read-only via the
  // project_members bucket — fanning one user's stuck analysis across every
  // teammate's browser queue on every login/app relaunch.
  const userId = await getInternalUserId()
  if (!userId) return 0
  const cutoffIso = new Date(Date.now() - RECONCILE_MAX_AGE_MS).toISOString()
  const rows = await ps.getAll<{ id: string }>(
    `SELECT id FROM analyses
      WHERE status IN ('pending_upload', 'pending_analysis')
        AND "imageUrl" IS NOT NULL
        AND "createdAt" > ?
        AND "userId" = ?
      ORDER BY "createdAt" DESC
      LIMIT ?`,
    [cutoffIso, userId, RECONCILE_LIMIT],
  )
  if (rows.length === 0) return 0

  // Only consult non-terminal intents — `done` and `failed` intents don't
  // protect against re-enqueue and loading them scales with device age.
  const [pendingRun, inFlightRun] = await Promise.all([
    listIntents({ type: 'run-analysis', status: 'pending' }),
    listIntents({ type: 'run-analysis', status: 'in_flight' }),
  ])
  const activeRunAnalysisIds = new Set<string>()
  for (const intent of [...pendingRun, ...inFlightRun]) {
    const aId = String((intent.payload as { analysisId?: unknown }).analysisId || '')
    if (aId) activeRunAnalysisIds.add(aId)
  }

  let enqueued = 0
  for (const row of rows) {
    if (activeRunAnalysisIds.has(row.id)) continue
    try {
      await enqueueIntent('run-analysis', { analysisId: row.id })
      enqueued++
    } catch (err) {
      logger.monitoring.warn('reconcile: enqueue failed', {
        analysisId: row.id,
        error: String(err),
      })
    }
  }

  if (enqueued > 0) {
    logger.intents.info('reconcile-enqueued', {
      candidates: rows.length,
      enqueued,
    })
    if (ConnectivityService.getInstance().isOnline) {
      getIntentWorker().scheduleTick()
    }
  }

  return enqueued
}

/**
 * Fast-path hook for callers that just enqueued: signal the worker to run now
 * without waiting for a tick timer. Safe to call from any enqueue path.
 */
export async function enqueueIntentAndRun<P extends Record<string, unknown>>(
  type: IntentType,
  payload: P,
  opts?: Parameters<typeof enqueueIntent>[2]
) {
  const intent = await enqueueIntent(type, payload, opts)
  trackIntent('intent_enqueued', { intentId: intent.id, type })
  if (ConnectivityService.getInstance().isOnline) {
    getIntentWorker().scheduleTick()
  }
  return intent
}

// Re-export the status type for consumers.
export type { IntentStatus }
